// @owners { team: patients-team }
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useSyncExternalStore } from 'react';
import { mapNextAvailableDateResponsesByPrescriptionId } from '~shared/features/next-available-date/helpers/mapNextAvailableDateResponsesToDates';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { type NextAvailableDate, type NextAvailableDateResponse } from '~shared/types/clients';

/**
 * Subscribes to the React Query cache and returns a `Record<number, NextAvailableDate>` keyed by prescription ID
 * across all checkout-scoped NAD cache entries.
 *
 * The returned reference is stable — it only updates when cached data changes.
 * Use as a direct prescription-ID lookup (strategy **A** reads) without needing the compound address+prescription key.
 */
export const useNextAvailableDatesByPrescriptionIdFromQueryCache = (): Record<number, NextAvailableDate> => {
  const queryClient = useQueryClient();
  // Compare at the raw-response level so the snapshot function is idempotent: mapping functions
  // create new objects on every call, so comparing mapped output would always see "changed" and
  // trigger an infinite re-render loop via useSyncExternalStore.
  const prevResponsesRef = useRef<NextAvailableDateResponse[]>([]);
  const snapshotRef = useRef<Record<number, NextAvailableDate>>({});

  return useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () => {
      const entries = queryClient.getQueriesData<NextAvailableDateResponse[]>({
        queryKey: nextAvailableDatesQueryKeys.checkout(),
      });
      const nextResponses = entries.flatMap(([, data]) => data ?? []);
      const prevResponses = prevResponsesRef.current;
      const changed =
        nextResponses.length !== prevResponses.length || nextResponses.some((r, i) => r !== prevResponses[i]);
      if (changed) {
        prevResponsesRef.current = nextResponses;
        snapshotRef.current = mapNextAvailableDateResponsesByPrescriptionId(nextResponses);
      }
      return snapshotRef.current;
    },
    () => snapshotRef.current,
  );
};
